const NON_NEGATIVE_DECIMAL = /^\d+(\.\d+)?$/;

export const isNonNegativeDecimalString = (value: string): boolean =>
  NON_NEGATIVE_DECIMAL.test(value.trim());

/**
 * The stored rate as a percentage, for display next to it: `0.0045` → `0.45`.
 *
 * The decimal point is shifted on the string rather than multiplied by 100,
 * because `0.0045 * 100` is `0.44999999999999996` in binary floating point.
 */
export const decimalToPercent = (value: string): string | null => {
  const trimmed = value.trim();
  if (!isNonNegativeDecimalString(trimmed)) return null;

  const [wholePart, fractionPart = ''] = trimmed.split('.');
  const pointIndex = wholePart.length + 2;
  const digits = (wholePart + fractionPart).padEnd(pointIndex, '0');

  const shiftedWhole = digits.slice(0, pointIndex).replace(/^0+(?=\d)/, '');
  const shiftedFraction = digits.slice(pointIndex).replace(/0+$/, '');

  return shiftedFraction ? `${shiftedWhole}.${shiftedFraction}` : shiftedWhole;
};

/**
 * Keeps a rate input to digits and a single decimal point — letters and signs
 * never reach the payload, where perp-engine's `Decimal` scalar would reject
 * them anyway.
 */
export const sanitizeDecimalInput = (value: string): string => {
  const cleaned = value.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');

  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

export interface ApiErrorInfo {
  status?: number;
  code?: string;
  messages: string[];
}

/**
 * `GlobalExceptionFilter` normalises everything to `{code, message, data}`, and
 * `message` is a string array for DTO validation failures only.
 */
export const parseApiError = (error: unknown): ApiErrorInfo => {
  const source = (error ?? {}) as {
    status?: number;
    message?: string;
    body?: { code?: string; message?: string | string[] };
  };
  const body = source.body;
  const rawMessage = body?.message;

  let messages: string[] = [];
  if (Array.isArray(rawMessage)) messages = rawMessage.filter((item) => !!item);
  else if (typeof rawMessage === 'string' && rawMessage) messages = [rawMessage];
  else if (typeof source.message === 'string' && source.message) messages = [source.message];

  return {
    status: source.status,
    code: typeof body?.code === 'string' ? body.code : undefined,
    messages,
  };
};

/**
 * The inverse of `decimalToPercent`: `30` → `0.3`, `5` → `0.05`. The decimal
 * point is shifted on the string, never multiplied, so `29` is `0.29` and not
 * `0.29000000000000004`. Returns null for anything that is not a non-negative
 * decimal (signs, exponents, empty).
 */
export const percentToDecimal = (value: string): string | null => {
  const trimmed = value.trim();
  if (!isNonNegativeDecimalString(trimmed)) return null;

  const [wholePart, fractionPart = ''] = trimmed.split('.');
  const padded = wholePart.padStart(3, '0');
  const shiftedWhole = padded.slice(0, -2).replace(/^0+(?=\d)/, '');
  const shiftedFraction = (padded.slice(-2) + fractionPart).replace(/0+$/, '');

  return shiftedFraction ? `${shiftedWhole}.${shiftedFraction}` : shiftedWhole;
};

/** Digits only — for whole-day and whole-second inputs. */
export const sanitizeIntegerInput = (value: string): string => value.replace(/\D/g, '');

/** How many digits follow the decimal point (`12.3400` → 4). */
export const decimalPlaces = (value: string): number => {
  const [, fraction = ''] = value.trim().split('.');
  return fraction.length;
};
